import * as Highcharts from 'highcharts';
import { getChartTheme } from './utils/chartTheme';
import { estimateStyleFor } from './home/shared';
import { on } from './util';

export default class {
    static init = () => {
        renderWeightChart();
        renderWaistChart();
        renderBmiChart();
        renderBiometricChart();
        renderIntakeOutputChart();
        initDateRangeNav();
    }
}

// ---------------------------------------------------------------------------
// Typed payload shapes -- match app/Presenters/BodyPresenter.php's five JSON
// globals character for character (288-06-SUMMARY.md's 11-key shape for the
// three Section 1 domains below; the Section 2/3 shapes join in Task 2). The
// client performs zero computation on any of these -- every point, every
// null, every marker, every trend value, every projection endpoint and every
// axis bound already arrived pre-computed from the presenter. This module
// only paints what the payload says and rebuilds a URL on date-input change.
// ---------------------------------------------------------------------------

interface BodyPointMarker {
    enabled: boolean;
    fillColor: string;
    lineColor: string;
    lineWidth: number;
    radius: number;
}

interface BodyPoint {
    x: number;
    y: number | null;
    custom: { estimate: 0 | 1 };
    marker?: BodyPointMarker;
}

interface BodyTrendPoint {
    x: number;
    y: number | null;
}

interface BodyProjectionPoint {
    x: number;
    y: number;
}

// Shared shape for body_weight_chart / body_waist_chart / body_bmi_chart --
// the 11-key payload 288-06-SUMMARY.md records, verified array_keys()-exact
// server-side.
interface BodySeriesPayload {
    key: string;
    label: string;
    unit: string;
    decimals: number;
    points: BodyPoint[];
    trend: BodyTrendPoint[];
    projection: BodyProjectionPoint[];
    zoneBoundaryX: number | null;
    hasData: boolean;
    yMin: number | null;
    yMax: number | null;
}

declare var body_weight_chart: BodySeriesPayload;
declare var body_waist_chart: BodySeriesPayload;
declare var body_bmi_chart: BodySeriesPayload;

// Section 2 (Biometric Context) -- the 9-key per-series shape 288-07-SUMMARY.md records. No
// custom.estimate concept exists on these three columns (no estimate flag), so points are plain
// {x, y} pairs, unlike the three Section 1 series above.
interface BiometricSeriesPayload {
    key: string;
    label: string;
    unit: string;
    decimals: number;
    axisIndex: number;
    points: BodyTrendPoint[];
    hasData: boolean;
    yMin: number | null;
    yMax: number | null;
}

// WR-01 (288 code review): Section 3's intake/output payloads carry no server-supplied
// `decimals` field (unlike Sections 1 and 2), so its tooltip interpolated the raw value and a
// non-integer SUM() rendered as e.g. "342.70000000000005 kcal". Round to 2dp and let String()
// strip trailing zeros, so 342.70000000000005 -> "342.7" and an integer stays an integer.
function fmtValue(v: number): string {
    return String(Math.round(v * 100) / 100);
}

interface BiometricChartPayload {
    series: BiometricSeriesPayload[];
}

declare var body_biometric_chart: BiometricChartPayload;

// Section 3 (Intake vs. Output) -- the shape 288-07-SUMMARY.md records: one intake series (its
// own estimate flag, like Section 1's points) and a list of independent output series (no
// estimate concept -- runs/oura_active_calories carry no per-day estimate flag of their own).
interface IntakePoint {
    x: number;
    y: number | null;
    custom: { estimate: 0 | 1 };
}

interface IntakeSeriesPayload {
    key: string;
    label: string;
    unit: string;
    points: IntakePoint[];
}

interface OutputSeriesPayload {
    key: string;
    label: string;
    unit: string;
    points: BodyTrendPoint[];
    isEmptyInWindow: boolean;
    globalFirstDate: string | null;
    globalLastDate: string | null;
}

interface IntakeOutputChartPayload {
    intake: IntakeSeriesPayload;
    output: OutputSeriesPayload[];
    outputHasData: boolean;
    outputYMin: number | null;
    outputYMax: number | null;
}

declare var body_intake_output_chart: IntakeOutputChartPayload;

// ---------------------------------------------------------------------------
// The three reused /home series colours, declared ONCE, here and only here.
// Byte-identical to HomeModel::getGraphDataArray()'s own 'colors' entries, as
// rendered today by src/js/home.ts. The PHP payload deliberately does not
// carry a colour (288-06-SUMMARY.md's own recorded split) -- this map is the
// single source for the "reused verbatim from /home" claim, beside the WHO
// BMI bands below which are reused the same way.
// ---------------------------------------------------------------------------
const SERIES_COLOR: Record<string, string> = {
    weight: '#BB72F4',
    waist: '#a4c639',
    bmi: '#20C997',
};

// ---------------------------------------------------------------------------
// NEW this phase -- never charted anywhere in this codebase before (D-13).
// average_hrv is deliberately NOT Weight's own hue in the map above -- reusing it would put two
// differently-meaning series on this one page in one colour.
// ---------------------------------------------------------------------------
const BIOMETRIC_COLOR: Record<string, string> = {
    vascular_age: '#5B8DEF',
    average_hrv: '#2CB1BC',
    lowest_heart_rate: '#FF5733',
};

// Intake: reused verbatim -- the solid cap colour of home/shared.ts's own ALC_COLOR_ESTIMATE
// gradient, i.e. alcohol's own real, non-estimate bar colour on /home today.
const INTAKE_COLOR = '#E1A818';

// Output: two new "burn" hues, deliberately distinct from every other colour on this page.
const OUTPUT_COLOR: Record<string, string> = {
    runs: '#FF7F50',
    oura_active_calories: '#2A9D8F',
};

// ---------------------------------------------------------------------------
// Zero-data axis guard (Phase 282's named gotcha; home.ts's own goal_forced
// branch is the shape this mirrors, same Highcharts-internals reasoning):
// axis.min/axis.max resolve from pick(hardMin, thresholdMin, dataMin) /
// pick(hardMax, thresholdMax, dataMax) BEFORE softMin/softMax are ever read --
// softMin/softMax sit behind an isNumber(axis.min) && isNumber(axis.max) gate
// and only WIDEN an already-numeric range. With zero real readings,
// dataMin/dataMax are undefined, that gate is false, and softMin/softMax are
// never read at all. Hard min/max feed pick() ahead of the gate, so they
// alone can establish extremes with no data -- the has-data case sets
// neither key and lets Highcharts auto-scale, which is correct once a chart
// has deep data throughout.
//
// Reused at every zero-data call site on this page (Section 1 per-domain,
// Section 2 per-biometric-series, Section 3's right axis) via this one
// function so the fix can never drift between them. Returns an object with
// BOTH keys present or NEITHER -- a key is never assigned a missing value.
// ---------------------------------------------------------------------------
export function zeroDataAxisBounds(
    hasData: boolean,
    yMin: number | null,
    yMax: number | null,
): Partial<Pick<Highcharts.YAxisOptions, 'min' | 'max'>> {
    if (!hasData && yMin !== null && yMax !== null) {
        return { min: yMin, max: yMax };
    }
    return {};
}

// ---------------------------------------------------------------------------
// One shared builder for all three Section 1 (Weight/Waist/BMI) charts.
// ---------------------------------------------------------------------------
function buildSectionOneChart(payload: BodySeriesPayload, containerId: string): void {
    if (!document.getElementById(containerId)) return;

    // The rolling-trend/projection line's own colour, read live at construction time -- the same
    // getComputedStyle/--ksat-accent technique rhythm.ts's own chart construction uses.
    const accentHex = getComputedStyle(document.documentElement).getPropertyValue('--ksat-accent').trim();

    const yAxis: Highcharts.YAxisOptions = {
        ...zeroDataAxisBounds(payload.hasData, payload.yMin, payload.yMax),
    };
    if (payload.key === 'bmi') {
        // D-11 / UI-SPEC Color section: WHO BMI category bands, byte-identical to
        // src/js/home.ts's own plotBands block (underweight/normal/overweight/obese).
        yAxis.plotBands = [
            { from: 0,    to: 18.5, color: 'rgba(96,165,250,0.10)' },  // underweight
            { from: 18.5, to: 25,   color: 'rgba(34,197,94,0.10)'  },  // normal
            { from: 25,   to: 30,   color: 'rgba(250,204,21,0.10)' },  // overweight
            { from: 30,   color: 'rgba(239,68,68,0.10)'  },            // obese — open-ended
        ] as Highcharts.YAxisPlotBandsOptions[];
    }

    // Series 2: the rolling trend and its own projection as ONE series (zones-based), so the
    // dashed segment can never visually detach from the solid one. When zoneBoundaryX is null the
    // projection is gated -- trend alone, no zones, no dashed segment (no undefined option keys
    // anywhere below).
    const trendSeries: Highcharts.SeriesSplineOptions = {
        type: 'spline',
        name: 'Trend',
        color: accentHex,
        lineWidth: 3,
        marker: { enabled: false },
        connectNulls: false,
        data: [...payload.trend, ...payload.projection] as Highcharts.SeriesSplineOptions['data'],
    };
    if (payload.zoneBoundaryX !== null) {
        trendSeries.zoneAxis = 'x';
        trendSeries.zones = [
            { value: payload.zoneBoundaryX },
            { dashStyle: 'Dash', color: accentHex },
        ];
    }

    Highcharts.chart(containerId, Highcharts.merge(getChartTheme(), {
        chart: { type: 'spline' },
        title: { text: '' },
        // datetime, no min, no max -- the dashed projection segment extends forward past the
        // last real point and a hard cap here would silently clip exactly that (the inverse of
        // the zero-data trap above: this chart has deep data, so the default auto-scale is right).
        xAxis: { type: 'datetime' },
        yAxis,
        legend: { enabled: false },
        plotOptions: {
            // The load-bearing half of BODY-02: an explicit null point from a cleared-sentinel
            // day or an unrecorded day renders as a real gap, never bridged by a straight line.
            spline: { connectNulls: false },
        },
        series: [
            {
                type: 'spline',
                name: payload.label,
                color: SERIES_COLOR[payload.key],
                connectNulls: false,
                // Passed straight through -- payload.points already carries custom.estimate and,
                // on estimate days, its complete server-built marker object. No post-processing,
                // no client-built marker.
                data: payload.points as Highcharts.SeriesSplineOptions['data'],
            },
            trendSeries,
        ],
        tooltip: {
            formatter: function (this: any): string {
                const dateStr = Highcharts.dateFormat('%Y-%m-%d', this.x);
                if (this.y === null || this.y === undefined) {
                    return `${dateStr}<br/>${this.series.name}: no reading`;
                }
                if (this.series.name !== payload.label) {
                    // The Trend series -- a plain value line, no estimate concept of its own.
                    return `${dateStr}<br/>${this.series.name}: ${(this.y as number).toFixed(payload.decimals)}${payload.unit}`;
                }
                // D-05's literal tooltip requirement -- the suffix appears on this series only.
                const suffix = this.point?.custom?.estimate === 1 ? ' (est.)' : '';
                return `${dateStr}<br/>${payload.label}: ${(this.y as number).toFixed(payload.decimals)}${payload.unit}${suffix}`;
            },
        },
        credits: { enabled: false },
    } as unknown as Highcharts.Options));
}

function renderWeightChart(): void {
    if (typeof body_weight_chart === 'undefined') return;
    buildSectionOneChart(body_weight_chart, 'body-weight-chart');
}

function renderWaistChart(): void {
    if (typeof body_waist_chart === 'undefined') return;
    buildSectionOneChart(body_waist_chart, 'body-waist-chart');
}

function renderBmiChart(): void {
    if (typeof body_bmi_chart === 'undefined') return;
    buildSectionOneChart(body_bmi_chart, 'body-bmi-chart');
}

// ---------------------------------------------------------------------------
// Section 2 -- Biometric Context. One chart, three series, three independent-unit axes (an
// age-equivalent number, milliseconds, beats per minute cannot share one scale). No trend, no
// projection, no estimate concept on these columns. Never reads bod_from/bod_to -- the payload
// itself is always built from each domain's own GLOBAL window (BodyPresenter's own contract),
// so this render function has nothing to narrow even if it wanted to.
// ---------------------------------------------------------------------------
function renderBiometricChart(): void {
    if (typeof body_biometric_chart === 'undefined') return;
    const containerId = 'body-biometric-chart';
    if (!document.getElementById(containerId)) return;

    const yAxis: Highcharts.YAxisOptions[] = body_biometric_chart.series.map(series => ({
        title: { text: series.label },
        gridLineWidth: series.axisIndex === 0 ? 0.1 : 0,
        opposite: series.axisIndex !== 0,
        ...zeroDataAxisBounds(series.hasData, series.yMin, series.yMax),
    }));

    Highcharts.chart(containerId, Highcharts.merge(getChartTheme(), {
        chart: { type: 'spline' },
        title: { text: '' },
        xAxis: { type: 'datetime' },
        yAxis,
        legend: { enabled: true },
        plotOptions: {
            spline: { connectNulls: false },
        },
        series: body_biometric_chart.series.map(series => ({
            type: 'spline',
            name: series.label,
            color: BIOMETRIC_COLOR[series.key],
            yAxis: series.axisIndex,
            connectNulls: false,
            data: series.points as Highcharts.SeriesSplineOptions['data'],
        })),
        tooltip: {
            formatter: function (this: any): string {
                const dateStr = Highcharts.dateFormat('%Y-%m-%d', this.x);
                if (this.y === null || this.y === undefined) {
                    return `${dateStr}<br/>${this.series.name}: no reading`;
                }
                const series = body_biometric_chart.series.find(s => s.label === this.series.name);
                const decimals = series ? series.decimals : 1;
                const unit = series ? series.unit : '';
                return `${dateStr}<br/>${this.series.name}: ${(this.y as number).toFixed(decimals)}${unit}`;
            },
        },
        credits: { enabled: false },
    } as unknown as Highcharts.Options));
}

// ---------------------------------------------------------------------------
// Section 3 -- Intake vs. Output. Two Y-axes: left "Drinks" (intake only), right "Calories out"
// (both output series, sharing one unit though never joined as one series). The right axis is
// this page's HIGHEST PRIORITY zero-data risk: the intake series keeps populating the left axis
// and drawing normally, so the chart looks populated and plausible while the right axis silently
// fails beside it unless the hard min/max guard below fires exactly when both output series are
// empty in the resolved window.
// ---------------------------------------------------------------------------
function renderIntakeOutputChart(): void {
    if (typeof body_intake_output_chart === 'undefined') return;
    const containerId = 'body-intake-output-chart';
    if (!document.getElementById(containerId)) return;

    const payload = body_intake_output_chart;

    // The only place on this page that applies a client-side estimate style -- the three Section 1
    // line series receive their complete marker object pre-built server-side instead.
    const intakePoints: Highcharts.PointOptionsObject[] = payload.intake.points.map(pt => {
        if (pt.custom.estimate !== 1) return pt as Highcharts.PointOptionsObject;
        return {
            ...pt,
            color: estimateStyleFor('alc'),
            borderWidth: 1,
            dashStyle: 'ShortDash' as Highcharts.DashStyleValue,
        } as Highcharts.PointOptionsObject;
    });

    // Both output series are built unconditionally from payload.output -- never filtered, never
    // showInLegend:false, never null-padded across the ~32-day runs->Oura gap. A dropped legend
    // entry would read as "this domain doesn't exist on this page", a different, false claim from
    // "this domain has nothing in the current view".
    const outputSeries: Highcharts.SeriesSplineOptions[] = payload.output.map(series => ({
        type: 'spline',
        name: series.label,
        color: OUTPUT_COLOR[series.key],
        yAxis: 1,
        connectNulls: false,
        data: series.points as Highcharts.SeriesSplineOptions['data'],
    }));

    // The right-axis guard, UI-SPEC's HIGHEST PRIORITY item. softMin/softMax cannot help here --
    // with both output series empty there is no dataMin/dataMax to widen from at all. The left
    // "Drinks" axis needs no guard: alc.qty stays near-gapless under any window.
    const rightAxis: Highcharts.YAxisOptions = {
        title: { text: 'Calories out (kcal)' },
        opposite: true,
        gridLineWidth: 0,
        ...zeroDataAxisBounds(payload.outputHasData, payload.outputYMin, payload.outputYMax),
    };

    Highcharts.chart(containerId, Highcharts.merge(getChartTheme(), {
        chart: { type: 'column' },
        title: { text: '' },
        xAxis: { type: 'datetime' },
        yAxis: [
            { title: { text: 'Drinks' }, gridLineWidth: 0.1 },
            rightAxis,
        ],
        legend: { enabled: true },
        plotOptions: {
            column: { connectNulls: false },
            spline: { connectNulls: false },
        },
        series: [
            {
                type: 'column',
                name: payload.intake.label,
                color: INTAKE_COLOR,
                yAxis: 0,
                data: intakePoints,
            },
            ...outputSeries,
        ],
        tooltip: {
            formatter: function (this: any): string {
                const dateStr = Highcharts.dateFormat('%Y-%m-%d', this.x);
                if (this.y === null || this.y === undefined) {
                    return `${dateStr}<br/>${this.series.name}: no reading`;
                }
                if (this.series.name === payload.intake.label) {
                    const suffix = this.point?.custom?.estimate === 1 ? ' (est.)' : '';
                    return `${dateStr}<br/>${payload.intake.label}: ${fmtValue(this.y)}${payload.intake.unit}${suffix}`;
                }
                const series = payload.output.find(s => s.label === this.series.name);
                return `${dateStr}<br/>${this.series.name}: ${fmtValue(this.y)}${series ? series.unit : ''}`;
            },
        },
        credits: { enabled: false },
    } as unknown as Highcharts.Options));
}

// ---------------------------------------------------------------------------
// The date-range re-navigation handler -- cloned from src/js/archetypes.ts verbatim,
// substituting arc_from/arc_to -> bod_from/bod_to. A real page reload, never an ajax call.
// Names only bod_from/bod_to in both directions; every other existing URL param carries through
// untouched since the handler rebuilds from the CURRENT search string rather than a blank one --
// it can preserve an unrelated param but can never originate one.
// ---------------------------------------------------------------------------
function initDateRangeNav(): void {
    on('change', 'input[name="bod_from"],input[name="bod_to"]', (element: HTMLInputElement) => {
        let from = element.name === 'bod_from' ? element.value : document.querySelector<HTMLInputElement>('input[name="bod_from"]')?.value;
        let to   = element.name === 'bod_to'   ? element.value : document.querySelector<HTMLInputElement>('input[name="bod_to"]')?.value;
        const params = new URLSearchParams(window.location.search);
        if (from) { params.set('bod_from', from); } else { params.delete('bod_from'); }
        if (to)   { params.set('bod_to', to); }     else { params.delete('bod_to'); }
        location.href = '?' + params.toString();
    });
}
